import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface Onglet<T extends string> {
  id: T;
  label: string;
  count?: number | null;
}

/**
 * Onglets accessibles (motif ARIA « tabs ») : flèches gauche/droite, Début, Fin.
 * Le contenu est rendu par l'appelant dans <TabPanel>.
 */
export function Tabs<T extends string>({
  onglets,
  actif,
  onChange,
  label,
}: {
  onglets: Onglet<T>[];
  actif: T;
  onChange: (id: T) => void;
  label: string;
}) {
  const base = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const clavier = (e: KeyboardEvent, index: number) => {
    const dernier = onglets.length - 1;
    const cible =
      e.key === 'ArrowRight' ? (index === dernier ? 0 : index + 1)
      : e.key === 'ArrowLeft' ? (index === 0 ? dernier : index - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? dernier
      : null;
    if (cible === null) return;
    e.preventDefault();
    onChange(onglets[cible]!.id);
    refs.current[cible]?.focus();
  };

  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {onglets.map((o, i) => (
        <button
          key={o.id}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={`${base}-${o.id}`}
          aria-selected={o.id === actif}
          tabIndex={o.id === actif ? 0 : -1}
          className={o.id === actif ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => onChange(o.id)}
          onKeyDown={(e) => clavier(e, i)}
        >
          {o.label}
          {o.count !== undefined && o.count !== null && <span className="tabs__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div role="tabpanel" aria-label={label} className="tabpanel" tabIndex={0}>
      {children}
    </div>
  );
}
